import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import Forces from '../math/Forces';
import SoundAssetKeys from '../SoundAssetKeys';
import BalloonContainer from './BalloonContainer';
import BalloonString from './BalloonString';

export default class Balloon {
  /**
   * 
   * @param {Phaser.Scene} scene 
   * @param {BalloonContainer} container 
   */
  constructor(scene, container,startx, starty) {
    this.scene = scene;
    this.container = container;

    this.stringX = 0;
    this.stringY = 0;

    // Pop stuff
    this.curAnimation = 0;
    this.animationCount = 2;
    this.topPrefix = 'Balloon_Top0';
    this.popType = null;
    this.animationWaiter = null;
    this.animationTime = 0.04;

    // Forces
    this.mass = 1;
    this.charge = 0;
    this.gravity = 50;
    this.kSpring = 10;
    this.cDamping = 0.9;

    // Incremental move
    this.angle = null;
    this.speed = null;
    this.ease = 0.13;
    this.distanceToTarget = 0;
    this.distanceTraveled = 0;
    this.direction = null;

    // p
    this.popScale = 1.2;
    this.popIncrement = 0.03;
    this.curScale = 1;
    this.isAlive = false;
    this.isActive = true;

    this.topLimit = 0;

    this.colorID = 3;

    this.popPrefix = `${this.topPrefix + this.colorID}_Pop`;
    this.balloonTop = ABCyaPhaser.Images.addImage(startx,starty, this.topPrefix + this.colorID, this.scene);
    this.balloonTopPop = ABCyaPhaser.Images.addImage(0,0,this.topPrefix + this.colorID, this.scene).setVisible(false);
    this.workingWidth = this.balloonTop.width;
    this.workingHeight = this.balloonTop.height;

    this.balloonString = new BalloonString(this.scene, startx, starty);
    this.balloonString.baseX = -2;
    this.balloonString.baseY = this.balloonTop.height / 2;

    if (this.container === null) {
      this.scene.add.existing(this.balloonString);
      this.scene.children.bringToTop(this.balloonTop);
      this.scene.children.bringToTop(this.balloonTopPop);
    }
    else {
      this.container.add([this.balloonString, this.balloonTop, this.balloonTopPop]);
    }

    // set up physics
    this.pos = new Phaser.Math.Vector2(startx, starty);
    this.vel = new Phaser.Math.Vector2(0, 0);
    this.acc = new Phaser.Math.Vector2();
    this.attractor = new Phaser.Math.Vector2();
    this.omega = Math.sqrt(this.kSpring / this.mass);
    this.A = new Phaser.Math.Vector2(this.pos.x, this.pos.y);
    this.A.subtract({ x: this.attractor.x, y: this.attractor.y });
    this.B = new Phaser.Math.Vector2(this.vel.x, this.vel.y);
    this.B.multiply({ x: 1 / this.omega, y: 1 / this.omega });
    this.displ = new Phaser.Math.Vector2();
    this.springForce = new Phaser.Math.Vector2();
    this.dampForce = new Phaser.Math.Vector2();
    this.bobForce = new Phaser.Math.Vector2();
    this.totalForce = new Phaser.Math.Vector2();

    this.timeElapsed = 0;

    this.startPos = new Phaser.Math.Vector2();
    this.destPos = new Phaser.Math.Vector2();
    this.bottomLimit = this.scene.scale.height + 200;
  }

  reset (x,y, colorID = 1) {
    this.colorID = colorID;
    this.topPrefix = `Balloon_Top0${this.colorID}`;
    this.balloonTop.setFrame(`Balloon_Top0${this.colorID}`);
    this.balloonTopPop.setFrame(`Balloon_Top0${this.colorID}`);
    this.balloonTopPop.scale = 1;
    this.isAlive = true;
    this.isActive = true;
    this.balloonString.resetRopePosition(x,y);
    this.balloonString.setVisible(true);
    this.balloonTop.setVisible(true);
    this.balloonTopPop.setVisible(false);
    this.curAnimation = 0;
  }

  initHoverFloat (x,y) {
    this.state = STATE_HOVER;
    this.attractor.x = x;
    this.attractor.y = y;
  }

  initMoveTo (destX, destY, speed = 12) {
    this.state = STATE_IM;
    this.startPos.x = this.balloonTop.x;
    this.pos.x = this.balloonTop.x;
    this.startPos.y = this.balloonTop.y;
    this.pos.y = this.balloonTop.y;
    this.destPos.x = destX;
    this.destPos.y = destY;
    this.speed = speed * 60;// multiply by 60 to get the speed per second instead of speed per frame
    this.ease = 0.8;
    this.angle = Math.atan2(destY - this.pos.y, destX - this.pos.x);
    this.distanceToTarget = Phaser.Math.Distance.BetweenPoints(this.pos, this.destPos);
    this.distanceTraveled = 0;
  }

  tick (deltaSeconds) {
    const d = deltaSeconds;
    this.timeElapsed += d;
    this.pos.add({ x: this.vel.x * d, y: this.vel.y * d });
    this.moveUpdate();

    // Check collisions
    switch (this.state) {
      case STATE_MOVE_TO:
        this.floatTick(d);
        break;

      case STATE_HOVER:
        this.floatTick(d);
        break;

      case STATE_IM:
        this.incrementalMove(d);
        break;

      case STATE_POPPING:
        this.popTick(d);
        break;

      case STATE_FALLING:
        this.fallTick(d);
        break;
    }

    this.balloonString.tick(deltaSeconds, this.pos.x + this.balloonString.baseX, this.pos.y + this.balloonString.baseY);
  }

  moveUpdate () {
    this.balloonTop.x = this.pos.x;
    this.balloonTop.y = this.pos.y;
  }

  floatTick (dt) {
    this.pos.add({ x: this.vel.x * dt, y: this.vel.y * dt });
    this.calcForce();
    this.applyAccel();
    this.applyVelocity(dt);
  }

  calcForce () {
    this.displ.set(this.pos.x - this.attractor.x, this.pos.y - this.attractor.y);

    Forces.spring(this.springForce, this.kSpring, this.displ);
    Forces.damping(this.cDamping, this.vel, this.dampForce);

    const maxx = 80;
    const maxy = 80;
    const x = (maxx * (Math.random() - 0.5)) * Math.cos(2 * this.omega * this.timeElapsed) + (maxx * (Math.random() - 0.5)) * Math.sin(2 * this.omega * this.timeElapsed);
    const y = (maxy * (Math.random() - 0.5)) * Math.cos(2 * this.omega * this.timeElapsed) + (maxy * (Math.random() - 0.5)) * Math.sin(2 * this.omega * this.timeElapsed);

    this.bobForce.set(x,y);

    Forces.add([this.springForce, this.dampForce, this.bobForce], this.totalForce);
  }

  applyAccel () {
    this.acc.set(this.totalForce.x, this.totalForce.y);
    this.acc.multiply({ x: 1 / this.mass,y: 1 / this.mass });
  }

  applyVelocity (d) {
    this.vel.add({ x: this.acc.x * d, y: this.acc.y * d });
  }

  incrementalMove (dt) {
    const workingX = this.pos.x + Math.cos(this.angle) * this.speed * this.ease * dt;
    const workingY = this.pos.y + Math.sin(this.angle) * this.speed * this.ease * dt;
    this.distanceTraveled = Phaser.Math.Distance.BetweenPoints(this.startPos, { x: workingX,y: workingY });
    if (this.distanceTraveled >= this.distanceToTarget) {
      this.balloonTop.emit('BALLOON_FINISHED', { balloon: this });
      this.pos.x = this.destPos.x;
      this.pos.y = this.destPos.y;
    }
    else {
      this.pos.x = workingX;
      this.pos.y = workingY;
    }
  }

  popTick (dt) {
    if (this.curScale < this.popScale) {
      this.curScale += this.popIncrement;
      this.balloonTopPop.scale = this.curScale;
    }
    else {
      this.balloonTopPop.scale = 1;
      this.vel.y = 520;
      this.vel.x = Phaser.Math.Between(10,20);
      this.curScale = 1;
      this.popType = Phaser.Math.Between(1,3) - 1;
      this.popPrefix = `${this.topPrefix}_Pop${this.popType}`;
      this.state = STATE_FALLING;
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._pop, this.scene);
    }
  }

  fallTick (dt) {
    if (this.animationWaiter <= 0) {
      this.curAnimation++;
      if (this.curAnimation > this.animationCount) {
        this.balloonTopPop.setVisible(false);
      }
      else {
        this.popType = Phaser.Math.Between(1,3) - 1;
        this.popPrefix = `${this.topPrefix}_Pop${this.popType}`;
        this.balloonTopPop.setFrame(this.popPrefix + this.curAnimation);
        this.animationWaiter = this.animationTime;
      }
    }
    else {
      this.animationWaiter -= dt;
    }
    if (this.pos.y > this.bottomLimit) {
      this.isActive = false;
      this.state = null;
    }
  }

  pop () {
    this.state = STATE_POPPING;
    this.animationWaiter = this.animationTime;
    this.balloonTopPop.x = this.pos.x;
    this.balloonTopPop.y = this.pos.y;
    this.balloonTop.setVisible(false);
    this.balloonTopPop.setVisible(true);
    this.isAlive = false;
  }

  initFloatTo (destX, destY) {
    this.attractor.x = destX;
    this.attractor.y = destY;
    this.kSpring = 1;
    this.cDamping = 2;
    this.state = STATE_MOVE_TO;
  }

  checkHit (x,y) {
    const pt = this.balloonTop.getLocalPoint(x,y);
    const alpha = this.scene.textures.getPixelAlpha(pt.x, pt.y, this.balloonTop.texture.key, this.balloonTop.frame.name);
    return alpha && alpha > 0;
  }

  hide () {
    this.balloonTop.setVisible(false);
    this.balloonTopPop.setVisible(false);
    this.balloonString.setVisible(false);
  }
}

const STATE_MOVE_TO = 10;
const STATE_IM = 11;
const STATE_HOVER = 20;
const STATE_POPPING = 40;
const STATE_FALLING = 41;